/* istanbul ignore file */
// TODO [LEARN-15811]: ^ ignore coverage on this file until we can have app test scaffolding ready
import React, { useMemo } from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import { withRouter } from 'react-router'

import { CurriculumRouteContentGetter as RouteContentGetter } from 'layers/content/v3/Hocs/CurriculumRouteContentGetter'
import withSiteContext from 'layers/content/v2/Hocs/withSiteContext'
import { navigationEvent } from 'layers/navigation/store/operations'
import withApiError from 'layers/errorHandling/apiError'

import { ErrorMessage } from 'secondstep-components'

import {
  isPreviewRoute,
  isTier2PreviewMode,
  isDigitalWellBeingPreviewMode,
} from 'utils/urlHelpers'
import { parseGradeAndProgram } from 'utils/lmsHelpers'
import { resolveRouteServiceApp } from 'utils/productHelpers'
import { selectors as contextSelectors } from 'auth/stores/userContext'

import { useUnitLessonCompletion } from 'hooks/useUnitLessonCompletion'

import UnitV3PageComponent from './component'
import { handleBackClick, handleTabChange } from './helper'
import { getClassTitle } from 'components/ProductLessons/classListHelpers'
import {
  extractLinkSummariesFromUnit,
  resolveCourseCode,
  resolveTrainingLinkSummary,
  validateClassId,
  getProductNameFromUrl,
} from './data'

/**
 * Maps curriculum-service contentGroupCode values to the corresponding
 * top-level key in the /v2/instructor/me response.  Add new programs here
 * when UnitV3 is adopted for additional product lines.
 */
const CONTENT_GROUP_TO_INSTRUCTOR_KEY = {
  dwb: 'Dwb',
}

/**
 * UnitV3 container.
 *
 * Replaces both the legacy `FlaggedRouteContentGetter` (content-service v2)
 * and the `FlaggedCourseManagerGetter` (course-manager) data sources with
 * a single `CurriculumRouteContentGetter` call. The curriculum-service
 * resolves the route + returns the unit page payload (and any associated
 * course pointer) in one round trip.
 *
 * For this PR we still render via the existing `UnitComponent` (lesson
 * accordion UI) — see `./component.jsx` for the rationale.
 */
export const UnitV3Container = props => {
  const {
    routeContent,
    match,
    location,
    navigationEvent: dispatchNavigationEvent,
    productTabTitle,
    userContext,
    currentSite,
    instructorData,
  } = props || {}

  const { content = {}, loading, route, contentGroupCode, coursePointer } =
    routeContent || {}
  const queryType = route?.queryType
  const unit =
    (queryType && content?.[queryType]) || content?.unit || content?.unitTi

  const classId = useMemo(() => {
    const params = new URLSearchParams(location?.search || '')
    return params.get('class') ?? undefined
  }, [location?.search])
  const gradeSegment = match?.params?.grade
  const courseCode = resolveCourseCode({
    coursePointer,
    unit,
    gradeSegment,
    contentGroupCode,
  })

  const instructorDataKey =
    CONTENT_GROUP_TO_INSTRUCTOR_KEY[contentGroupCode?.toLowerCase()] || null

  // Preview routes must never hit the LMS (/v2/instructor/me). Suppress the
  // instructor data key on any preview route so the completion hook skips its
  // instructor fetch entirely. Teach routes are unaffected.
  const completionInstructorDataKey = isPreviewRoute()
    ? null
    : instructorDataKey

  const trainingLinkSummary = resolveTrainingLinkSummary(unit)
  const baseLinkSummaries = extractLinkSummariesFromUnit({
    unit,
    trainingLinkSummary,
  })

  const {
    linkSummaries,
    isLoading: isCompletionLoading,
  } = useUnitLessonCompletion({
    instructorDataKey: completionInstructorDataKey,
    linkSummaries: baseLinkSummaries,
    siteId: currentSite?.siteId,
    gradeSegment,
    courseCode,
    coursePointer,
    contentGroupCode,
  })

  const isTeachRoute = match?.params?.verb === 'teach'
  // Use the hook's loading state when a known instructorDataKey is available;
  // fall back to the prop from withSiteContext for unmapped programs so their
  // teach routes still gate rendering until data arrives.
  const isInstructorDataLoading =
    isTeachRoute &&
    (instructorDataKey
      ? isCompletionLoading
      : !instructorData || Object.keys(instructorData).length === 0)

  const readyToLoad = !loading && !!unit && !isInstructorDataLoading

  if (!readyToLoad) {
    return null
  }

  let selectedClassRegistration = null
  if (isTeachRoute) {
    const validation = validateClassId({
      instructorData,
      classId,
      siteId: currentSite?.siteId,
      courseCode,
      gradeSegment,
    })

    if (!validation.isValid) {
      const productName = getProductNameFromUrl(location?.pathname || '')
      return (
        <ErrorMessage
          additionalDetails={`Please create and select a valid class from the [/product/${productName}/lessons](/product/${productName}/lessons) page.`}
          statusCode={400}
        />
      )
    }

    selectedClassRegistration = validation.registration
  }

  const classTitle = getClassTitle(selectedClassRegistration)

  const { programKey: productName } = parseGradeAndProgram(match?.url) || {}
  const isPreview = isTier2PreviewMode() || isDigitalWellBeingPreviewMode()
  const declareTabChange = handleTabChange({
    location,
    navigationEvent: dispatchNavigationEvent,
  })
  const declareBackClick = handleBackClick({
    location,
    navigationEvent: dispatchNavigationEvent,
  })

  return (
    <UnitV3PageComponent
      classInstanceId={classId}
      classTitle={classTitle}
      isPreview={isPreview}
      linkSummaries={linkSummaries}
      location={location}
      match={match}
      navigationEvent={dispatchNavigationEvent}
      onBacklinkClick={declareBackClick}
      onSelectUnit={declareTabChange}
      productName={productName}
      productTabTitle={productTabTitle}
      trainingLinkSummary={trainingLinkSummary}
      unit={unit}
      userContext={userContext}
    />
  )
}

UnitV3Container.propTypes = {
  currentSite: PropTypes.shape({
    siteId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  }),
  instructorData: PropTypes.object,
  location: PropTypes.object,
  match: PropTypes.object,
  navigationEvent: PropTypes.func,
  productTabTitle: PropTypes.string,
  routeContent: PropTypes.shape({
    content: PropTypes.object,
    loading: PropTypes.bool,
    route: PropTypes.object,
  }),
  userContext: PropTypes.object,
}

const mapStateToProps = state => {
  const { productManager } = state || {}

  return {
    productTabTitle: productManager?.productMap?.name || '',
    userContext: contextSelectors.selectProgramContext(state),
  }
}

const mapDispatchToProps = {
  navigationEvent,
}

const options = {
  app: ({ route }) => resolveRouteServiceApp(route),
  routeResolver: () => window?.location?.pathname,
}

const HydratedContainer = withRouter(
  connect(mapStateToProps, mapDispatchToProps)(UnitV3Container),
)

export default withSiteContext(
  RouteContentGetter(withApiError(HydratedContainer), options),
)
